import { LinkWithSlug } from "@/router";

import {
  Notification,
  NotificationLevel,
  NotificationType,
  useDailyUsageTotalSummaryQuery,
} from "@/apollo/types";
import {
  Button,
  ButtonGroup,
  PrimaryButton,
} from "@/components/elements/Button";
import Tooltip from "@/components/elements/Tooltip";
import { UpgradeNowButton } from "@/components/modules/UpgradeNowButton";
import {
  useAccountNotifications,
  useRemoveNotification,
} from "@/features/notifications";
import { WithRole } from "@/features/roleAccess";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useToast } from "@/providers/ToastProvider";
import { formatPercent, formatRowCountCompact } from "@/shared/formatters";
import { ComponentProps } from "@tw-classed/react";
import { CheckIcon, InfoIcon } from "lucide-react";

import { WorkspaceAlert, WorkspaceAlertResolver } from "../useWorkspaceAlerts";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

type MarUsageDetail = {
  usage: {
    usageRatio: number;
    overageRows: number;
    usedRows: number;
    rowsLimit: number;
  };
  periodStart: string;
  periodEnd: string;
};

function isMarUsageDetail(detail: unknown): detail is MarUsageDetail {
  if (detail == null || typeof detail !== "object") return false;
  const d = detail as Record<string, unknown>;
  if (
    typeof d.periodStart !== "string" ||
    typeof d.periodEnd !== "string" ||
    d.usage == null ||
    typeof d.usage !== "object"
  )
    return false;
  const u = d.usage as Record<string, unknown>;
  return typeof u.usedRows === "number" && typeof u.rowsLimit === "number";
}

function ResolveButton({
  notification,
  ...props
}: ComponentProps<typeof Button> & {
  notification: Notification;
}) {
  const toast = useToast();
  const mixpanel = useMixpanel();

  const [removeNotification, loading] = useRemoveNotification({
    onCompleted: () => {
      toast(
        "Success",
        "Syncs that were previously blocked by this issue will be retried",
        "success",
      );
    },
    onError: () => {
      toast(
        "Error",
        "An error occurred while marking this issue as resolved. Please try again later.",
        "error",
      );
    },
  });

  return (
    <Tooltip content="Click to mark this issue as resolved. Upon doing so, any syncs that were previously blocked by this issue will be retried.">
      <span>
        <WithRole action="resolveNotification">
          <PrimaryButton
            size="xs"
            onClick={() => {
              mixpanel.track("Account Level Notification Resolve Clicked", {
                location: "workspace-banner",
                notificationType: notification.type,
              });
              removeNotification(notification);
            }}
            icon={<CheckIcon />}
            isLoading={loading}
            loadingText="Resolving..."
            {...props}
          >
            Resolve
          </PrimaryButton>
        </WithRole>
      </span>
    </Tooltip>
  );
}

export const useAccountLevelNotificationsResolver: WorkspaceAlertResolver =
  () => {
    const { notifications } = useAccountNotifications();

    function createAlertProps(
      notification: Notification,
    ): WorkspaceAlert | null {
      const status = (
        {
          [NotificationLevel.Info]: "info",
          [NotificationLevel.Warning]: "warning",
          [NotificationLevel.Error]: "error",
        } as Record<NotificationLevel, WorkspaceAlert["status"]>
      )[notification.notificationLevel];

      const defaultProps = {
        id: notification.id,
        type: notification.type,
        updatedAt: new Date(notification.updatedAt),
        status,
        message: notification.messageData?.title ?? notification.message,
        action: notification.isDismissable ? (
          <ResolveButton notification={notification} />
        ) : undefined,
      };

      switch (notification.type) {
        case NotificationType.GithubAppUninstalled:
        case NotificationType.GithubRepositoryRemoved: {
          return {
            ...defaultProps,
            message: notification.message,
            action: (
              <ButtonGroup size="xs" className="flex gap-4">
                <Button as={LinkWithSlug} to="/settings/github">
                  View GitHub settings
                </Button>
                {defaultProps.action}
              </ButtonGroup>
            ),
          };
        }
        case NotificationType.GithubSyncIncomplete: {
          return null;
        }
        case NotificationType.MarUsageThresholdAlert: {
          if (notification.notificationLevel === NotificationLevel.Info) {
            return null;
          }

          const alert = {
            ...defaultProps,
            isDismissible: true,
            dismissDuration:
              notification.notificationLevel === NotificationLevel.Error
                ? 2 * ONE_DAY_MS
                : undefined,
          };

          const detail = notification.detail;
          if (!isMarUsageDetail(detail)) {
            return alert;
          }

          return {
            ...alert,
            message: (
              <MarThresholdAlertMessage
                rowsLimit={detail.usage.rowsLimit}
                periodStart={detail.periodStart}
                periodEnd={detail.periodEnd}
              />
            ),
            action: (
              <Button size="xs" as={LinkWithSlug} to="/settings/plan">
                View plan
              </Button>
            ),
          };
        }
        case NotificationType.BigqueryBilling: {
          return {
            ...defaultProps,
            action: (
              <ButtonGroup size="xs" className="flex gap-4">
                <Button
                  as="a"
                  target="_blank"
                  href="https://weld.app/docs/destinations/google-bigquery/troubleshooting#billing-disabled"
                  variant="solid"
                  colorScheme="secondary"
                  icon={<InfoIcon />}
                  className="border-black/20 dark:border-white/20"
                >
                  More info
                </Button>
                {defaultProps.action}
              </ButtonGroup>
            ),
          };
        }
        case NotificationType.FreeTrialExpired: {
          return {
            ...defaultProps,
            title: "Hey, your free trial is over.",
            message:
              "To keep using our paid features, upgrade to one of our paid plans.",
            action: (
              <UpgradeNowButton size="xs" source="trial expired banner" />
            ),
          };
        }
        case NotificationType.ManagedBqUsageExceededSoftLimit: {
          return {
            ...defaultProps,
            action: (
              <WithRole action="updateDataWarehouse">
                <Button
                  as={LinkWithSlug}
                  to={`/settings/data-warehouse`}
                  colorScheme="primary"
                  variant="solid"
                  size="xs"
                >
                  Migrate Datawarehouse
                </Button>
              </WithRole>
            ),
            isDismissible: true,
            dismissDuration: 2 * ONE_DAY_MS,
          };
        }
        case NotificationType.OverdueInvoiceServiceSuspension:
        case NotificationType.OverdueInvoiceReminder: {
          return {
            ...defaultProps,
            message: notification.messageData.title,
            action: (
              <WithRole action="manageBilling">
                <Button
                  as={LinkWithSlug}
                  to="/settings/plan/billing"
                  hash="invoices"
                  colorScheme="primary"
                  variant="solid"
                  size="xs"
                >
                  View invoice
                </Button>
              </WithRole>
            ),
            isDismissible: defaultProps.status !== "error",
            dismissDuration: ONE_DAY_MS,
          };
        }
        default: {
          return defaultProps;
        }
      }
    }
    return notifications
      .map(createAlertProps)
      .filter((x): x is WorkspaceAlert => x !== null);
  };

function MarThresholdAlertMessage(props: {
  periodStart: string;
  periodEnd: string;
  rowsLimit: number;
}) {
  const { data, loading } = useDailyUsageTotalSummaryQuery({
    variables: {
      period: {
        start: props.periodStart,
        end: props.periodEnd,
      },
    },
    skip: !props.periodStart || !props.periodEnd,
  });

  const summary = data?.dailyUsageTotalSummary.data;
  if (loading) {
    return <span>Fetching your current monthly row usage...</span>;
  }

  if (!summary) {
    return <span>Unable to retrieve your current monthly row usage.</span>;
  }

  const usageRatio =
    props.rowsLimit > 0 ? summary.activeRows / props.rowsLimit : 0;
  const overageRows = Math.max(0, summary.activeRows - props.rowsLimit);
  return (
    <span>
      You've used {formatRowCountCompact(summary.activeRows)} of your{" "}
      {formatRowCountCompact(props.rowsLimit)} monthly rows (
      {formatPercent(usageRatio)})
      {overageRows > 0 && (
        <> — {formatRowCountCompact(overageRows)} rows over your limit</>
      )}
      .
    </span>
  );
}
